import { proxy } from 'valtio';
import {
  buildSignInUrl,
  buildSignUpVerifyUrl,
  clearAuthModalUrl,
} from '../../app/constants/authRoutes';

type PendingAction = () => unknown | Promise<unknown>;

interface IModalStore {
  signUpModalOpen: boolean;
  signInModalOpen: boolean;
  verifyCodeModalOpen: boolean;
  resetPwdModalOpen: boolean;
  forgotPwdModalOpen: boolean;
  currentEmail: string | null;
  pendingAction: PendingAction | null;
}

export type OpenSignInModalOptions = {
  email?: string;
  pendingAction?: PendingAction;
};

function buildInPlaceSignInUrl(email?: string): string {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  params.set('signin', 'true');
  if (email?.trim()) {
    params.set('email', email.trim());
  }
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ''}${hash}`;
}

const modalStore = proxy<IModalStore>({
  signUpModalOpen: false,
  signInModalOpen: false,
  verifyCodeModalOpen: false,
  resetPwdModalOpen: false,
  forgotPwdModalOpen: false,
  currentEmail: null,
  pendingAction: null,
});

const modalActions = {
  setCurrentEmail: (email: string) => {
    modalStore.currentEmail = email;
  },
  openSignUpModal: () => {
    modalStore.signUpModalOpen = true;
  },
  closeSignUpModal: () => {
    modalStore.signUpModalOpen = false;
    clearAuthModalUrl();
  },

  openSignInModal: (options?: PendingAction | OpenSignInModalOptions) => {
    let email: string | undefined;
    let pendingAction: PendingAction | undefined;

    if (typeof options === 'function') {
      pendingAction = options;
    } else if (options) {
      email = options.email;
      pendingAction = options.pendingAction;
    }

    modalStore.signInModalOpen = true;
    if (pendingAction) {
      modalStore.pendingAction = pendingAction;
    }
    if (email) {
      modalStore.currentEmail = email;
    }
    window.history.replaceState(
      {},
      '',
      pendingAction
        ? buildInPlaceSignInUrl(email ?? modalStore.currentEmail ?? undefined)
        : buildSignInUrl(email ?? modalStore.currentEmail ?? undefined),
    );
  },
  closeSignInModal: () => {
    modalStore.signInModalOpen = false;
    modalStore.pendingAction = null;
    clearAuthModalUrl();
  },

  openVerifyCodeModal: (email?: string) => {
    modalStore.verifyCodeModalOpen = true;
    if (email) {
      modalStore.currentEmail = email;
    }
    window.history.replaceState(
      {},
      '',
      buildSignUpVerifyUrl(email ?? modalStore.currentEmail ?? ''),
    );
  },
  closeVerifyCodeModal: () => {
    modalStore.verifyCodeModalOpen = false;
    clearAuthModalUrl();
  },

  openResetPwdModal: () => {
    modalStore.resetPwdModalOpen = true;
  },
  closeResetPwdModal: () => {
    modalStore.resetPwdModalOpen = false;
    clearAuthModalUrl();
  },

  openForgotPwdModal: () => {
    modalStore.forgotPwdModalOpen = true;
  },
  closeForgotPwdModal: () => {
    modalStore.forgotPwdModalOpen = false;
  },

  // Utility functions
  closeAllModals: () => {
    modalStore.signUpModalOpen = false;
    modalStore.signInModalOpen = false;
    modalStore.verifyCodeModalOpen = false;
    modalStore.resetPwdModalOpen = false;
    modalStore.forgotPwdModalOpen = false;
    clearAuthModalUrl();
  },

  // Switch between modals
  switchToSignIn: (email?: string) => {
    modalStore.signUpModalOpen = false;
    modalStore.verifyCodeModalOpen = false;
    modalStore.resetPwdModalOpen = false;
    modalStore.forgotPwdModalOpen = false;
    modalStore.signInModalOpen = true;
    const resolvedEmail = email ?? modalStore.currentEmail ?? undefined;
    window.history.replaceState({}, '', buildSignInUrl(resolvedEmail));
  },

  switchToSignUp: () => {
    modalStore.signInModalOpen = false;
    modalStore.verifyCodeModalOpen = false;
    modalStore.resetPwdModalOpen = false;
    modalStore.forgotPwdModalOpen = false;
    modalStore.signUpModalOpen = true;
    clearAuthModalUrl();
  },

  switchToVerifyCode: (email?: string) => {
    modalStore.signUpModalOpen = false;
    modalStore.signInModalOpen = false;
    modalStore.resetPwdModalOpen = false;
    modalStore.forgotPwdModalOpen = false;
    modalStore.verifyCodeModalOpen = true;
    if (email?.trim()) {
      modalStore.currentEmail = email.trim();
    }
    window.history.replaceState(
      {},
      '',
      buildSignUpVerifyUrl(email?.trim() ?? modalStore.currentEmail ?? ''),
    );
  },

  // Initialize from URL params / auth paths
  initializeFromParams: (params: {
    signin?: string;
    isSignInPath?: boolean;
    isVerifyCode?: boolean;
    isResetPwd?: boolean;
    email?: string;
  }) => {
    modalStore.signInModalOpen = params.signin === 'true' || params.isSignInPath === true;
    modalStore.verifyCodeModalOpen = !!params.isVerifyCode;
    modalStore.resetPwdModalOpen = !!params.isResetPwd;
    if (params.email) {
      modalStore.currentEmail = params.email;
    }
  },
  resetEmail: () => {
    modalStore.currentEmail = null;
  },

  executePendingAction: async () => {
    const action = modalStore.pendingAction;
    modalStore.pendingAction = null;
    if (action) {
      await action();
    }
  },
};

export { modalStore, modalActions };
